import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const GoogleAuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');
      const stateParam = urlParams.get('state');

      if (errorParam) {
        setStatus('error');
        setError('Google sign-in was cancelled or failed.');
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setError('No authorization code received.');
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
        return;
      }

      try {
        // Parse state if available
        let returnUrl = '/';
        let referral = null;
        if (stateParam) {
          try {
            const state = JSON.parse(atob(stateParam));
            returnUrl = state.returnUrl || '/';
            referral = state.referral || null;
          } catch (e) {
            console.log('Could not parse state');
          }
        }

        // Exchange code for tokens
        const response = await axios.post(`${API}/auth/google-oauth-code`, {
          code,
          redirect_uri: window.location.origin + '/auth/google/callback',
          referral: referral
        });

        const { session_token } = response.data;
        localStorage.setItem('session_token', session_token);
        
        setStatus('success');
        
        // Redirect to main app
        setTimeout(() => {
          window.location.href = returnUrl;
        }, 1000);

      } catch (err) {
        console.error('Auth error:', err);
        
        if (err.response?.status === 202) {
          setStatus('waitlist');
          setError('Account created! You\'ve been added to our waitlist.');
        } else if (err.response?.status === 403) {
          setStatus('pending');
          setError('Your account is pending approval.');
        } else {
          setStatus('error');
          setError(err.response?.data?.detail || 'Authentication failed.');
        }

        setTimeout(() => {
          window.location.href = '/';
        }, 4000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Signing you in...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we complete your sign-in.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-2">
              Sign-in Successful!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Redirecting you to the dashboard...
            </p>
          </>
        )}

        {status === 'waitlist' && (
          <>
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">
              Welcome!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {error}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              We'll notify you once your account is approved.
            </p>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="text-6xl mb-6">⏳</div>
            <h2 className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
              Account Pending
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {error}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-6xl mb-6">❌</div>
            <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">
              Sign-in Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {error}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              Redirecting you back...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
